import { useEffect, useRef, useState } from "react";
import { fetchOrderMarginSummary, type OrderMarginRow } from "./economicSummaryApi";

/** Batch massimo per POST /api/economics/order-summary: il servizio calcola
 *  in loop per-ordine (query param per SKU), teniamo le richieste corte. */
const CHUNK_SIZE = 100;

export type OrderMarginsResult = {
  rows: ReadonlyMap<string, OrderMarginRow>;
  /** ISO 8601 o null: freschezza del mirror parametri AM (max synced_at),
   *  mostrata nella sezione «Margine (AM)» della sotto-card (Piano 3). */
  paramsSyncedAt: string | null;
};

/** Margini per-reference per le card della lista ordini (Davide 2026-07-03).
 *
 *  Best-effort e incrementale: fetcha SOLO le reference mai viste (cache
 *  accumulata per sessione di pagina — i filtri rimescolano la lista ma i
 *  margini non cambiano), a chunk, e ignora gli errori (la card semplicemente
 *  non mostra la riga margine). Ritorna la mappa reference → OrderMarginRow;
 *  le righe non calcolabili restano in mappa per non rifetcharle. */
export function useOrderMargins(references: string[], enabled: boolean): OrderMarginsResult {
  const cacheRef = useRef<Map<string, OrderMarginRow>>(new Map());
  const pendingRef = useRef<Set<string>>(new Set());
  const [snapshot, setSnapshot] = useState<ReadonlyMap<string, OrderMarginRow>>(new Map());
  const [paramsSyncedAt, setParamsSyncedAt] = useState<string | null>(null);

  // Chiave stabile: refetch solo quando compaiono reference nuove.
  const refsKey = references.join("|");

  useEffect(() => {
    if (!enabled) return;
    const missing = references.filter(
      (ref) => ref && !cacheRef.current.has(ref) && !pendingRef.current.has(ref),
    );
    if (missing.length === 0) return;
    let alive = true;

    void (async () => {
      for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
        if (!alive) return;
        const chunk = missing.slice(i, i + CHUNK_SIZE);
        // Pending marcato PER-CHUNK, subito prima del suo fetch (review
        // 2026-07-03 MEDIUM): marcare tutto il batch upfront lasciava i
        // chunk mai processati bloccati in pending quando l'effect veniva
        // rimpiazzato (cambio filtri) a meta' di un fetch multi-chunk.
        chunk.forEach((ref) => pendingRef.current.add(ref));
        try {
          const summary = await fetchOrderMarginSummary(chunk);
          for (const row of summary.orders) {
            cacheRef.current.set(row.reference, row);
          }
          if (summary.paramsSyncedAt) {
            setParamsSyncedAt(summary.paramsSyncedAt);
          }
        } catch {
          // Best-effort: senza margini la card mostra solo il totale. Le
          // reference del chunk fallito restano fuori cache: il retry
          // avviene solo quando nella lista compaiono reference NUOVE
          // (refsKey cambia), non a ogni refresh degli stessi ordini.
        } finally {
          chunk.forEach((ref) => pendingRef.current.delete(ref));
        }
        // Si pubblica SEMPRE, anche a effect gia' rimpiazzato: ``alive`` e'
        // false pure quando cambiano filtri/ricerca a componente montato, e
        // in quel caso il giro nuovo ha gia' fatto early return (le reference
        // erano ancora ``pending``, quindi ``missing`` vuoto). Fermandosi qui
        // i margini appena scaricati non li pubblicava piu' nessuno e la riga
        // «Margine netto» spariva dalle card fino alla comparsa di una
        // reference mai vista. Il setState dopo l'unmount e' un no-op (React
        // 18); il ciclo si ferma comunque al controllo in cima al ``for``.
        setSnapshot(new Map(cacheRef.current));
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refsKey riassume references
  }, [refsKey, enabled]);

  return { rows: snapshot, paramsSyncedAt };
}
